import React from 'react';
import { Typography, Space, Tag, QRCode } from 'antd';
import { 
  UserOutlined, 
  AppstoreOutlined,
  FireOutlined,
  ShopOutlined
} from '@ant-design/icons';

const { Text } = Typography;

interface DishLabelProps {
  data: {
    orderItem: {
      id: number;
      quantity: number;
      price: number;
      isScanned: boolean;
      scannedAt?: string;
    };
    menuItem: {
      name: string;
      weight: number;
      calories?: number | null;
      isVegan: boolean;
      isLenten: boolean;
      isHalal: boolean;
    };
    order: {
      id: number;
      customerCompany: string;
      supplierCompany: string;
      supplierPhone: string;
    };
    orderedBy: string;
    qrCode: string;
    totalWeight: number;
    totalCalories: number;
    specialAttributes: string[];
  };
}

const DishLabel: React.FC<DishLabelProps> = ({ data }) => {
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={{ 
      width: '60mm', 
      minHeight: '80mm', 
      padding: '6px', 
      border: '2px solid #000',
      backgroundColor: '#fff',
      fontFamily: 'monospace',
      fontSize: '11px',
      lineHeight: '1.1'
    }}>
      {/* Заголовок */}
      <div style={{ textAlign: 'center', marginBottom: '6px' }}>
        <Text strong style={{ fontSize: '14px' }}>
          🍽️ {data.menuItem.name}
        </Text>
        <br />
        <Text style={{ fontSize: '12px' }}>
          Заказ #{data.order.id}
        </Text>
      </div>

      {/* QR-код */}
      <div style={{ textAlign: 'center', marginBottom: '6px' }}>
        <QRCode 
          value={data.qrCode} 
          size={80}
          style={{ border: '1px solid #000' }}
        />
      </div>

      {/* Информация о заказчике */}
      <div style={{ marginBottom: '6px', textAlign: 'center' }}>
        <Text style={{ fontSize: '10px' }}>
          <ShopOutlined /> {data.order.customerCompany}
        </Text>
        <br />
        <Text style={{ fontSize: '10px' }}>
          <UserOutlined /> {data.orderedBy}
        </Text>
      </div>

      {/* Количество и цена */}
      <div style={{ marginBottom: '6px', textAlign: 'center' }}>
        <Text strong style={{ fontSize: '12px' }}>
          Количество: {data.orderItem.quantity} шт
        </Text>
        <br />
        <Text style={{ fontSize: '11px' }}>
          Цена: {data.orderItem.price}₽
        </Text>
      </div>

      {/* Особые атрибуты */}
      {data.specialAttributes.length > 0 && (
        <div style={{ marginBottom: '6px', textAlign: 'center' }}>
          <Space size={2} wrap>
            {data.specialAttributes.map((attr, index) => (
              <Tag key={index} color="blue">
                {attr}
              </Tag>
            ))}
          </Space>
        </div>
      )}

      {/* Пищевая ценность */}
      <div style={{ marginBottom: '6px', textAlign: 'center' }}>
        <Text style={{ fontSize: '10px' }}>
          <AppstoreOutlined /> {data.totalWeight}г
        </Text>
        <br />
        <Text style={{ fontSize: '10px' }}>
          <FireOutlined /> {data.totalCalories}ккал
        </Text>
      </div>

      {/* Статус сканирования */}
      <div style={{ textAlign: 'center', marginBottom: '6px' }}>
        {data.orderItem.isScanned ? (
          <Tag color="green">
            ✅ Отсканировано
          </Tag>
        ) : (
          <Tag color="orange">
            ⏳ Ожидает
          </Tag>
        )}
        {data.orderItem.scannedAt && (
          <div>
            <Text style={{ fontSize: '9px' }}>
              {formatDate(data.orderItem.scannedAt)}
            </Text>
          </div>
        )}
      </div>

      {/* Информация о поставщике */}
      <div style={{ textAlign: 'center', borderTop: '1px solid #ccc', paddingTop: '4px' }}>
        <Text style={{ fontSize: '9px' }}>
          🏪 {data.order.supplierCompany}
        </Text>
        <br />
        <Text style={{ fontSize: '9px' }}>
          📞 {data.order.supplierPhone}
        </Text>
      </div>
    </div>
  );
};

export default DishLabel;
